import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { List, X, Heart, Leaf, FacebookLogo, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services & Pricing" },
  { to: "/book", label: "Book Online" },
  { to: "/track", label: "Track Order" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact" },
  { to: "/faq", label: "FAQ" },
];

export const Logo = ({ light = false }) => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
    <img src="/zaekids-logo.png" alt="Zaekids Laundromat" className="h-12 w-12 shrink-0 rounded-full object-cover sm:h-14 sm:w-14" />
    <span className="leading-none">
      <span className={`font-script text-3xl font-bold ${light ? "text-brand-yellow" : "text-brand-green"}`}>Zaekids</span>
      <span className={`block text-[10px] font-extrabold tracking-[0.3em] ${light ? "text-white" : "text-brand-green-light"}`}>LAUNDROMAT</span>
    </span>
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 border-b border-brand-green/10 bg-white/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
        <Logo />
        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `rounded-full px-3 py-2 text-sm font-bold transition-colors duration-200 ${isActive ? "bg-soft-green text-brand-green" : "text-slate-600 hover:text-brand-green"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          {user ? (
            <button data-testid="header-account-btn" onClick={() => navigate(user.role === "admin" ? "/admin" : "/account")}
              className="btn-bounce hidden rounded-full border-2 border-brand-green px-4 py-2 text-sm font-extrabold text-brand-green sm:block">
              {user.role === "admin" ? "Dashboard" : "My Account"}
            </button>
          ) : user === false ? (
            <button data-testid="header-login-btn" onClick={() => navigate("/login")}
              className="btn-bounce hidden rounded-full border-2 border-brand-green px-4 py-2 text-sm font-extrabold text-brand-green sm:block">
              Log In
            </button>
          ) : null}
          <button data-testid="header-book-btn" onClick={() => navigate("/book")}
            className="btn-bounce rounded-full bg-brand-green px-5 py-2.5 text-sm font-extrabold text-white hover:shadow-[0_8px_20px_rgb(249,197,17,0.35)]">
            Book a Pick Up
          </button>
          <button data-testid="mobile-menu-btn" className="text-brand-green lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={28} /> : <List size={28} />}
          </button>
        </div>
      </div>
      {open && (
        <nav data-testid="mobile-menu" className="border-t border-brand-green/10 bg-white px-6 py-4 lg:hidden">
          {navLinks.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block rounded-xl px-3 py-2.5 font-bold text-slate-700 hover:bg-soft-green hover:text-brand-green">
              {l.label}
            </NavLink>
          ))}
          {user ? (
            <>
              <NavLink to={user.role === "admin" ? "/admin" : "/account"} onClick={() => setOpen(false)} className="block rounded-xl px-3 py-2.5 font-bold text-brand-green">
                {user.role === "admin" ? "Dashboard" : "My Account"}
              </NavLink>
              <button onClick={() => { logout(); setOpen(false); }} className="block w-full rounded-xl px-3 py-2.5 text-left font-bold text-red-600">Log Out</button>
            </>
          ) : (
            <NavLink to="/login" onClick={() => setOpen(false)} className="block rounded-xl px-3 py-2.5 font-bold text-brand-green">Log In / Sign Up</NavLink>
          )}
        </nav>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="bg-brand-green text-white">
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
      <div className="grid gap-10 md:grid-cols-4">
        <div>
          <Logo light />
          <p className="mt-4 text-sm leading-relaxed text-green-100">
            Clean Clothes. Happy Life. <Heart size={14} weight="fill" className="inline text-brand-yellow" /><br />
            Laundromat pick up and delivery across Western Sydney, NSW — wash, dry, fold, iron & steam done with love.
          </p>
        </div>
        <div>
          <h3 className="mb-3 font-extrabold text-brand-yellow">Quick Links</h3>
          {navLinks.map((l) => (
            <Link key={l.to} to={l.to} className="block py-1 text-sm text-green-100 hover:text-brand-yellow">{l.label}</Link>
          ))}
        </div>
        <div>
          <h3 className="mb-3 font-extrabold text-brand-yellow">Turnaround</h3>
          <p className="text-sm leading-7 text-green-100">24–48 hour turnaround<br />Pick up &amp; delivery: 5:00 PM – 7:00 PM</p>
        </div>
        <div>
          <h3 className="mb-3 font-extrabold text-brand-yellow">Get In Touch</h3>
          <p className="py-1 text-sm text-green-100">Located in Hebersham, NSW</p>
          <a href="tel:+61406586239" data-testid="footer-phone-link"
            className="mt-1 flex items-center gap-2 text-sm text-green-100 hover:text-brand-yellow">
            <Phone size={18} weight="duotone" /> 0406 586 239
          </a>
          <a href="mailto:Zaekidslaundromat@gmail.com" data-testid="footer-email-link"
            className="mt-1 flex items-center gap-2 break-all text-sm text-green-100 hover:text-brand-yellow">
            <EnvelopeSimple size={18} weight="duotone" /> Zaekidslaundromat@gmail.com
          </a>
          <a href="https://www.facebook.com/search/top?q=Zaekids%20Laundromat" target="_blank" rel="noopener noreferrer" data-testid="footer-facebook-link"
            className="mt-1 flex items-center gap-2 text-sm text-green-100 hover:text-brand-yellow">
            <FacebookLogo size={18} weight="duotone" /> Zaekids Laundromat
          </a>
        </div>
      </div>
      <div className="mt-10 flex flex-col items-center justify-between gap-2 border-t border-white/15 pt-6 text-xs text-green-200 sm:flex-row">
        <p>© {new Date().getFullYear()} Zaekids Laundromat · NSW, Australia</p>
        <p className="flex items-center gap-1"><Leaf size={14} weight="duotone" className="text-brand-yellow" /> Eco-friendly laundry, every load.</p>
      </div>
    </div>
  </footer>
);

export default function Layout() {
  return (
    <div className="App flex min-h-screen flex-col bg-brand-cream">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}
